import React, {useState} from 'react'
import {useRouter} from 'next/router'
import {Controller, useForm} from 'react-hook-form'
import styled from 'styled-components'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  Flex,
  Radio,
  Stack,
  Text,
  TextInput,
  useToast,
} from '@sanity/ui'
import {Membership} from '@sanity/access-api'
import {FormField} from '@/ui/index'
import {TokenContainer} from '@/ui/views/project/api/TokenContainer'
import {ProjectIcon} from '@/ui/components/project/projectIcon'
import {useCreateOrgToken} from './queries'
import {useCurrentScopeContext} from '@/context/useCurrentScopeContext'
import {Project} from '@/types/index'

const StyledDialog = styled(Dialog)`
  [data-ui='DialogCard'] {
    width: 100%;
    max-width: 600px;
  }
`

// Constants
const DIALOG_IDS = {
  TOKEN_CONTAINER: 'token-container-dialog',
  ADD_TOKEN: 'add-token-dialog',
} as const

const ERROR_MESSAGES = {
  REQUIRED_FIELD: 'This field is required',
  SELECT_PERMISSION: 'Please choose at least one permission',
  NO_PERMISSIONS: 'No available permissions',
} as const

const FORM_EXAMPLES = {
  NAME_DESCRIPTION: 'Examples: "SDK Apps Deployment", "Deploy Studios".',
  PERMISSIONS_DESCRIPTION: 'Choose the access privileges for the token.',
} as const

type PermissionSelection = {
  optionId: string
  role?: 'viewer' | 'editor'
  projectIds?: string[]
}

interface PermissionOption {
  id: string
  label: string
  description: string
  value: Membership
  projectRole?: string
  availableProjects?: Project[]
  viewerRole?: string
  editorRole?: string
}

interface FormData {
  label: string
  selectedPermissions: PermissionSelection[]
}

interface PermissionField {
  value: PermissionSelection[]
  onChange: (value: PermissionSelection[]) => void
}

const usePermissionHelpers = (field: PermissionField, optionId: string) => {
  const selection = field.value.find((s) => s.optionId === optionId)
  const isEnabled = !!selection

  const updatePermission = (newSelection: PermissionSelection | null) => {
    const otherPermissions = field.value.filter((s) => s.optionId !== optionId)
    if (newSelection) {
      field.onChange([...otherPermissions, newSelection])
    } else {
      field.onChange(otherPermissions)
    }
  }

  return {selection, isEnabled, updatePermission}
}

const getPermissionOptions = (orgId: string, orgProjects: Project[]): PermissionOption[] => {
  return [
    {
      id: 'sdk-app-editor',
      label: 'Manage SDK Apps',
      description: 'Deploy, read and delete SDK Applications',
      value: {
        resourceType: 'organization',
        resourceId: orgId,
        roleNames: ['sdk-app-editor-robot'],
      },
    },
    {
      id: 'media-library',
      label: 'Media Library',
      description: 'Access to Media Library',
      viewerRole: 'media-library-viewer-robot',
      editorRole: 'media-library-editor-robot',
      value: {
        resourceType: 'organization',
        resourceId: orgId,
        roleNames: [],
      },
    },
    {
      id: 'canvas',
      label: 'Canvas',
      description: 'Access to Canvas',
      viewerRole: 'canvas-viewer-robot',
      editorRole: 'canvas-editor-robot',
      value: {
        resourceType: 'organization',
        resourceId: orgId,
        roleNames: [],
      },
    },
    {
      id: 'deploy-studios',
      label: 'Deploy Studios (Token only)',
      description: 'Access to deploy Sanity Studio and GraphQL APIs',
      projectRole: 'deploy-studio',
      availableProjects: orgProjects,
      value: {
        resourceType: 'organization',
        resourceId: orgId,
        roleNames: ['deploy-studio-robot'],
      },
    },
  ]
}

const RoleCheckbox = ({
  label,
  description,
  ...restProps
}: {label: string; description: string} & Omit<React.HTMLProps<HTMLInputElement>, 'ref'>) => {
  return (
    <Flex align="center" as="label">
      <Checkbox {...restProps} />
      <Box marginLeft={3}>
        <Stack space={2}>
          <Box marginBottom={description ? 1 : 0}>
            <Text size={1} weight="semibold">
              {label}
            </Text>
          </Box>
          {description && (
            <Box>
              <Text size={1} muted>
                {description}
              </Text>
            </Box>
          )}
        </Stack>
      </Box>
    </Flex>
  )
}

const RoleRadioSelector = ({
  label,
  description,
  viewerRole,
  editorRole: _editorRole,
  field,
  value,
  optionId,
}: {
  label: string
  description: string
  viewerRole: string
  editorRole: string
  field: PermissionField
  value: Membership
  optionId: string
}) => {
  const {selection, isEnabled, updatePermission} = usePermissionHelpers(field, optionId)

  const handleRoleChange = (role: 'viewer' | 'editor') => {
    updatePermission({optionId, role})
  }

  const handlePermissionToggle = (checked: boolean) => {
    if (checked) {
      updatePermission({optionId, role: 'viewer'})
    } else {
      updatePermission(null)
    }
  }

  return (
    <Stack space={3}>
      <RoleCheckbox
        label={label}
        description={description}
        checked={isEnabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          handlePermissionToggle(e.target.checked)
        }}
      />

      <Box paddingLeft={6} paddingTop={2}>
        <Flex align="center" gap={4}>
          <Flex align="center" as="label">
            <Radio
              name={`role-${value.resourceId}-${viewerRole}`}
              checked={selection?.role === 'viewer'}
              disabled={!isEnabled}
              onChange={() => isEnabled && handleRoleChange('viewer')}
            />
            <Box marginLeft={3}>
              <Text size={1} style={{opacity: !isEnabled ? 0.5 : 1}}>
                Viewer
              </Text>
            </Box>
          </Flex>

          <Flex align="center" as="label">
            <Radio
              name={`role-${value.resourceId}-${viewerRole}`}
              checked={selection?.role === 'editor'}
              disabled={!isEnabled}
              onChange={() => isEnabled && handleRoleChange('editor')}
            />
            <Box marginLeft={3}>
              <Text size={1} style={{opacity: !isEnabled ? 0.5 : 1}}>
                Editor
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Stack>
  )
}

const ProjectSelector = ({
  label,
  description,
  projects,
  option,
  field,
}: {
  label: string
  description: string
  projects: Project[]
  option: PermissionOption & {projectRole: string}
  field: PermissionField
}) => {
  const {
    selection: _selection,
    isEnabled,
    updatePermission,
  } = usePermissionHelpers(field, option.id)
  const [accessType, setAccessType] = useState<'all' | 'select'>('all')
  const [selectedProjects, setSelectedProjects] = useState<string[]>([])

  const handlePermissionToggle = (checked: boolean) => {
    if (checked) {
      if (accessType === 'all') {
        updatePermission({optionId: option.id})
      } else if (accessType === 'select') {
        updatePermission({optionId: option.id, projectIds: selectedProjects})
      }
    } else {
      updatePermission(null)
    }
  }

  const handleAccessTypeChange = (newAccessType: 'all' | 'select') => {
    setAccessType(newAccessType)

    if (isEnabled) {
      if (newAccessType === 'all') {
        setSelectedProjects([])
        updatePermission({optionId: option.id})
      } else {
        updatePermission({optionId: option.id, projectIds: selectedProjects})
      }
    }
  }

  const handleProjectToggle = (projectId: string, checked: boolean) => {
    const newSelectedProjects = checked
      ? [...selectedProjects, projectId]
      : selectedProjects.filter((id) => id !== projectId)

    setSelectedProjects(newSelectedProjects)

    if (isEnabled && accessType === 'select') {
      updatePermission({optionId: option.id, projectIds: newSelectedProjects})
    }
  }

  return (
    <Stack space={3}>
      <RoleCheckbox
        label={label}
        description={description}
        checked={isEnabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          handlePermissionToggle(e.target.checked)
        }}
      />

      <Box paddingLeft={6} paddingTop={2}>
        <Stack space={4}>
          <Stack space={3}>
            <Text size={1} weight="medium">
              All Projects
            </Text>
            <Flex align="center" as="label">
              <Radio
                name={`access-type-${option.id}`}
                checked={accessType === 'all'}
                disabled={!isEnabled}
                onChange={() => handleAccessTypeChange('all')}
              />
              <Box marginLeft={3}>
                <Text size={1} muted>
                  Access to all existing and new projects in the organization
                </Text>
              </Box>
            </Flex>
          </Stack>

          <Stack space={3}>
            <Text size={1} weight="medium">
              Select Projects
            </Text>
            <Flex align="center" as="label">
              <Radio
                name={`access-type-${option.id}`}
                checked={accessType === 'select'}
                disabled={!isEnabled}
                onChange={() => handleAccessTypeChange('select')}
              />
              <Box marginLeft={3}>
                <Text size={1} muted>
                  Grant access to individual projects
                </Text>
              </Box>
            </Flex>

            {accessType === 'select' && (
              <Box paddingLeft={0} paddingTop={2}>
                <Card border padding={0} radius={2} tone={!isEnabled ? 'transparent' : 'default'}>
                  {projects.map((project, index) => (
                    <Box
                      key={project.id}
                      style={{
                        borderBottom:
                          index < projects.length - 1
                            ? '1px solid var(--card-border-color)'
                            : 'none',
                      }}
                    >
                      <Flex
                        align="center"
                        as="label"
                        padding={3}
                        style={{cursor: isEnabled ? 'pointer' : 'not-allowed'}}
                      >
                        <Checkbox
                          checked={selectedProjects.includes(project.id)}
                          disabled={!isEnabled}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleProjectToggle(project.id, e.target.checked)
                          }}
                        />
                        <Box marginLeft={3}>
                          <Flex align="center" gap={2}>
                            <ProjectIcon project={project} size={1} />
                            <Text size={1} style={{opacity: !isEnabled ? 0.5 : 1}}>
                              {project.displayName || project.id}
                            </Text>
                          </Flex>
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </Card>
              </Box>
            )}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  )
}

export const TokenCreationFormDialog = ({onClose}: {onClose: () => void}) => {
  const {mutate: createToken, isPending: isCreating} = useCreateOrgToken()
  const [token, setToken] = useState<string | null>(null)
  const toast = useToast()
  const {org: orgData} = useCurrentScopeContext()

  const orgProjects = orgData?.projects || []

  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
  } = useForm<FormData>({
    defaultValues: {
      label: '',
      selectedPermissions: [],
    },
  })

  const {
    query: {orgId: organizationId},
  } = useRouter()

  const permissionOptions = getPermissionOptions(organizationId as string, orgProjects)

  const convertToMemberships = (selectedPermissions: PermissionSelection[]): Membership[] => {
    const memberships: Membership[] = []

    selectedPermissions.forEach((selection) => {
      const option = permissionOptions.find((opt) => opt.id === selection.optionId)
      if (!option) return

      if (selection.role && option.viewerRole && option.editorRole) {
        const role = selection.role === 'viewer' ? option.viewerRole : option.editorRole
        memberships.push({
          ...option.value,
          roleNames: [role],
        })
        return
      }

      if (selection.projectIds && selection.projectIds.length > 0 && option.projectRole) {
        selection.projectIds.forEach((projectId) => {
          memberships.push({
            resourceType: 'project',
            resourceId: projectId,
            roleNames: [option.projectRole!],
          })
        })
        return
      }

      if (
        option.projectRole &&
        selection.projectIds !== undefined &&
        selection.projectIds.length === 0
      ) {
        return
      }

      if (option.value.roleNames.length > 0) {
        memberships.push(option.value)
      }
    })

    return memberships
  }

  const onSubmit = (data: FormData) => {
    const memberships = convertToMemberships(data.selectedPermissions)

    createToken(
      {
        label: data.label,
        memberships: memberships.reduce((acc: Membership[], membership) => {
          const existingMembership = acc.find(
            (m) =>
              m.resourceId === membership.resourceId && m.resourceType === membership.resourceType
          )
          if (existingMembership) {
            existingMembership.roleNames = [
              ...new Set([...existingMembership.roleNames, ...membership.roleNames]),
            ]
            return acc
          }
          return [...acc, membership]
        }, []),
      },
      {
        onSuccess: (data) => {
          setToken(data.data.token)
        },
        onError: (error) => {
          toast.push({
            title: error.message,
            status: 'error',
          })
        },
      }
    )
  }

  if (token) {
    return (
      <StyledDialog
        id={DIALOG_IDS.TOKEN_CONTAINER}
        onClose={onClose}
        header="Save your API key"
        footer={
          <Flex justify="flex-start" paddingX={4} paddingY={3}>
            <Button onClick={onClose} text="Close" />
          </Flex>
        }
      >
        <TokenContainer token={token} tone="default" />
      </StyledDialog>
    )
  }

  return (
    <StyledDialog
      id={DIALOG_IDS.ADD_TOKEN}
      onClose={onClose}
      header="Add API token"
      footer={
        <Flex gap={2} paddingX={4} paddingY={3} justify="flex-start">
          <Button
            loading={isCreating}
            onClick={handleSubmit(onSubmit)}
            text="Save token"
            autoFocus
          />
          <Button mode="ghost" tone="default" onClick={onClose} text="Cancel" />
        </Flex>
      }
    >
      <Box padding={4}>
        <Stack space={4}>
          <FormField
            label="Name"
            description={FORM_EXAMPLES.NAME_DESCRIPTION}
            error={errors.label ? ERROR_MESSAGES.REQUIRED_FIELD : ''}
          >
            <TextInput
              padding={3}
              autoComplete="off"
              {...register('label', {
                required: true,
              })}
            />
          </FormField>
          <FormField
            label="Permissions"
            as="fieldset"
            description={FORM_EXAMPLES.PERMISSIONS_DESCRIPTION}
            error={errors.selectedPermissions ? ERROR_MESSAGES.SELECT_PERMISSION : ''}
          >
            {permissionOptions.length > 0 ? (
              <Controller
                control={control}
                name="selectedPermissions"
                rules={{
                  validate: (value) => value.length > 0,
                }}
                render={({field}) => (
                  <Stack space={4} paddingTop={4}>
                    {permissionOptions.map((option) => {
                      if (option.projectRole) {
                        return (
                          <ProjectSelector
                            key={`${option.value.resourceType}-${option.value.resourceId}-${option.projectRole}`}
                            label={option.label}
                            description={option.description}
                            projects={option.availableProjects ?? []}
                            option={option as typeof option & {projectRole: string}}
                            field={field}
                          />
                        )
                      }

                      if (option.viewerRole && option.editorRole) {
                        return (
                          <RoleRadioSelector
                            key={`${option.value.resourceType}-${option.value.resourceId}-${option.viewerRole}`}
                            label={option.label}
                            description={option.description}
                            viewerRole={option.viewerRole}
                            editorRole={option.editorRole}
                            field={field}
                            value={option.value}
                            optionId={option.id}
                          />
                        )
                      }

                      return (
                        <RoleCheckbox
                          key={`${option.value.resourceType}-${option.value.resourceId}-${option.value.roleNames[0] || option.label}`}
                          label={option.label}
                          description={option.description}
                          checked={field.value.some((s) => s.optionId === option.id)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const currentPermissions = field.value || []
                            if (e.target.checked) {
                              field.onChange([...currentPermissions, {optionId: option.id}])
                            } else {
                              field.onChange(
                                currentPermissions.filter((s) => s.optionId !== option.id)
                              )
                            }
                          }}
                        />
                      )
                    })}
                  </Stack>
                )}
              />
            ) : (
              <Card tone="caution" padding={3}>
                <Text size={1} weight="semibold">
                  {ERROR_MESSAGES.NO_PERMISSIONS}
                </Text>
              </Card>
            )}
          </FormField>
        </Stack>
      </Box>
    </StyledDialog>
  )
}
